import type { InspectorAvailabilityResult } from './types';

import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { InspectorAvailabilityGridRow } from './inspector-availability-grid-row';
import { InspectorAvailabilityGridSkeletonLoading } from './inspector-availability-grid-skeleton-loading';

const headerCellSx = {
  px: 2,
  height: 32,
  borderBottom: '1px solid',
  borderColor: 'divider',
  typography: 'body2',
  fontWeight: 600,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
};

interface InspectorAvailabilityGridProps {
  loading?: boolean;
  results?: InspectorAvailabilityResult[];
  showChanges?: boolean;
  skeletonRowCount?: number;
  loadingMessage?: string;
  emptyMessage?: string;
  onDateClick?: (result: InspectorAvailabilityResult) => void;
}

export const InspectorAvailabilityGrid = memo(
  ({
    loading = false,
    results = [],
    showChanges = false,
    skeletonRowCount,
    loadingMessage,
    emptyMessage,
    onDateClick,
  }: InspectorAvailabilityGridProps) => {
    const { t } = useTranslation('shuffle');

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <InspectorAvailabilityGridSkeletonLoading
            message={loadingMessage}
            rowCount={skeletonRowCount}
          />
        ) : results.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              py: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {emptyMessage ?? t('inspectorAvailabilityGrid.empty')}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 240 }}>
                    {t('inspectorAvailabilityGrid.columns.name')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('inspectorAvailabilityGrid.columns.competencies')}
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 80 }}>
                    {t('inspectorAvailabilityGrid.columns.travel')}
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 80 }}>
                    {t('inspectorAvailabilityGrid.columns.businessUnit')}
                  </TableCell>
                  {showChanges ? (
                    <TableCell sx={{ ...headerCellSx, width: 80 }}>
                      {t('inspectorAvailabilityGrid.columns.changes')}
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ ...headerCellSx, width: 100 }}>
                    {t('inspectorAvailabilityGrid.columns.date')}
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120 }}>
                    {t('inspectorAvailabilityGrid.columns.action')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result) => (
                  <InspectorAvailabilityGridRow
                    key={result.id}
                    result={result}
                    showChanges={showChanges}
                    onDateClick={onDateClick}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }
);

InspectorAvailabilityGrid.displayName = 'InspectorAvailabilityGrid';
